import React, { useState, useEffect, useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import { ThumbsUp, MessageCircle, Eye, Check } from 'lucide-react';
import ModernPagination from '../ui/ModernPagination';
import FeedbackStatusBadge from './FeedbackStatusBadge';

const FeedbacksClosedModern = ({ feedbacks = [], isLoading, onViewDetail }) => {
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([{ id: 'likes', desc: true }]);

    // Load CSS dynamically
    useEffect(() => {
        const linkId = 'feedbacks-modern-css';
        if (!document.getElementById(linkId)) {
            const link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            link.href = 'assets/css/FeedbacksModern.css';
            document.head.appendChild(link);
        }
    }, []);

    // Column Definitions
    const columns = useMemo(
        () => [
            {
                accessorKey: 'feedback_id',
                header: '#',
                size: 60,
                cell: ({ getValue }) => (
                    <span style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '0.875rem' }}>
                        {getValue()}
                    </span>
                ),
            },
            {
                accessorKey: 'titel',
                header: 'Titel',
                size: 300,
                cell: ({ row }) => {
                    const feedback = row.original;
                    return (
                        <div>
                            <div
                                style={{
                                    fontWeight: feedback.user_has_read ? 400 : 600,
                                    color: 'var(--text-primary, #2c3e50)',
                                    marginBottom: '4px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => onViewDetail?.(feedback.feedback_id)}
                            >
                                {feedback.titel}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FeedbackStatusBadge status={3} size="small" />
                                {feedback.comment_count > 0 && (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            fontSize: '0.75rem',
                                            color: 'var(--text-muted, #94a3b8)',
                                        }}
                                    >
                                        <MessageCircle size={12} />
                                        {feedback.comment_count}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                accessorKey: 'user_name',
                header: 'Eingereicht von',
                size: 180,
                cell: ({ getValue }) => (
                    <span style={{ color: 'var(--text-secondary, #64748b)' }}>
                        {getValue()}
                    </span>
                ),
            },
            {
                accessorKey: 'datum_formatted',
                header: 'Datum',
                size: 120,
                cell: ({ getValue }) => (
                    <span style={{ color: 'var(--text-secondary, #64748b)' }}>
                        {getValue()}
                    </span>
                ),
            },
            {
                accessorKey: 'likes',
                header: 'Likes',
                size: 80,
                cell: ({ getValue }) => (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: 'var(--text-muted, #94a3b8)',
                            fontWeight: 500,
                        }}
                    >
                        <ThumbsUp size={14} />
                        {getValue()}
                    </span>
                ),
            },
            {
                id: 'actions',
                header: '',
                size: 100,
                cell: ({ row }) => {
                    const feedback = row.original;
                    return (
                        <div className="row-actions" style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            {/* Likes (disabled for closed) */}
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 8px',
                                    border: '1px solid var(--border-light, #e2e8f0)',
                                    borderRadius: '6px',
                                    background: 'var(--bg-secondary, #f5f7f9)',
                                    color: 'var(--text-muted, #94a3b8)',
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                }}
                                title="Erledigte Feedbacks können nicht mehr geliked werden"
                            >
                                <ThumbsUp size={14} />
                                {feedback.likes}
                            </span>

                            {/* View Detail Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewDetail?.(feedback.feedback_id);
                                }}
                                title="Details anzeigen"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '32px',
                                    padding: '6px',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '8px',
                                    background: 'transparent',
                                    color: 'var(--text-secondary, #64748b)',
                                    cursor: 'pointer',
                                }}
                            >
                                <Eye size={16} />
                            </button>
                        </div>
                    );
                },
                enableSorting: false,
                enableGlobalFilter: false,
            },
        ],
        [onViewDetail]
    );

    // Table Instance
    const table = useReactTable({
        data: feedbacks,
        columns,
        state: {
            sorting,
            globalFilter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    if (isLoading) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Laden...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="feedbacks-modern">
            <div className="table-container" style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                {/* Table Controls */}
                <div className="table-controls" style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid var(--border-light, #e2e8f0)' }}>
                    <div className="table-controls-left">
                        <div className="search-box" style={{ minWidth: '250px' }}>
                            <svg className="search-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Feedback durchsuchen..."
                                value={globalFilter ?? ''}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #64748b)' }}>
                        {table.getFilteredRowModel().rows.length} Einträge
                    </div>
                </div>

                {/* Table */}
                <div className="table-wrapper">
                    <table>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className={header.column.getCanSort() ? 'sortable' : ''}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{ width: header.column.getSize() }}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getCanSort() && (
                                                        <span className="sort-indicator">
                                                            {{
                                                                asc: '\u2191',
                                                                desc: '\u2193',
                                                            }[header.column.getIsSorted()] ?? '\u2195'}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={columns.length}
                                        style={{
                                            textAlign: 'center',
                                            padding: '2rem',
                                            color: 'var(--text-muted, #94a3b8)',
                                        }}
                                    >
                                        Keine abgeschlossenen Feedbacks vorhanden
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => onViewDetail?.(row.original.feedback_id)}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} onClick={(e) => cell.column.id === 'actions' && e.stopPropagation()}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {table.getPageCount() > 1 && (
                    <div className="pagination" style={{ padding: '0.75rem 1.25rem' }}>
                        <div className="pagination-info">
                            Zeige {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                            {Math.min(
                                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                table.getFilteredRowModel().rows.length
                            )}{' '}
                            von {table.getFilteredRowModel().rows.length}
                        </div>
                        <div className="pagination-controls">
                            <ModernPagination
                                currentPage={table.getState().pagination.pageIndex}
                                totalPages={table.getPageCount()}
                                onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
                                canPreviousPage={table.getCanPreviousPage()}
                                canNextPage={table.getCanNextPage()}
                                onPreviousPage={() => table.previousPage()}
                                onNextPage={() => table.nextPage()}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeedbacksClosedModern;
